import { ACQUIRE_ERROR_LABELS_PL, type AcquireErrorCode } from '@anisync/types';
import type { ToastTone } from '../components/ui/toast';
import type { BatchProgressItem } from './api';

/**
 * Odcinki, które widz uruchomił i które jeszcze nie są gotowe.
 *
 * Po kliknięciu „Odtwórz" droga do obrazu ma do trzech etapów: szukanie kopii
 * w źródłach (z Shinden do pół minuty), pobieranie i przygotowanie. Odtwarzacz
 * pokazuje je na pełnym ekranie, ale widz może z niego wyjść — wrócić do listy,
 * zaznaczyć kolejne odcinki — i wtedy znikała jedyna wiadomość o tym, co się
 * dzieje. Śledzony odcinek ma powiadomienie, które idzie za nim przez etapy
 * i kończy się „Oglądaj", gdy materiał jest gotowy.
 *
 * Magazyn w pamięci karty, nie w `localStorage`: to stan bieżącej wizyty, a po
 * odświeżeniu lista odcinków i kolejka pokazują to samo bez śledzenia.
 */

export interface TrackedMedia {
  mediaFileId: string;
  /** „Frieren — odc. 9" — to, co widz rozpozna bez kontekstu ekranu. */
  label: string;
  /** Dokąd prowadzi „Oglądaj" — adres odtwarzacza z tytułem i numerem. */
  href: string;
  /**
   * Powiadomienie, które już stoi — to z chwili kliknięcia. Śledzenie podmienia
   * jego treść zamiast dokładać drugie, więc widz widzi jeden komunikat od
   * kliknięcia do „Oglądaj".
   */
  toastId?: string;
}

let tracked = new Map<string, TrackedMedia>();
let snapshot: TrackedMedia[] = [];
const listeners = new Set<() => void>();

function publish(next: Map<string, TrackedMedia>): void {
  tracked = next;
  snapshot = [...next.values()];
  for (const listener of listeners) listener();
}

export function subscribeTracked(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function trackedSnapshot(): TrackedMedia[] {
  return snapshot;
}

export function trackMedia(media: TrackedMedia): void {
  const next = new Map(tracked);
  next.set(media.mediaFileId, media);
  publish(next);
}

export function untrackMedia(mediaFileId: string): void {
  if (!tracked.has(mediaFileId)) return;
  const next = new Map(tracked);
  next.delete(mediaFileId);
  publish(next);
}

/** Identyfikator powiadomienia odcinka — jeden toast prowadzi go przez etapy. */
export function mediaToastId(mediaFileId: string): string {
  return `media:${mediaFileId}`;
}

export interface ProgressMessage {
  tone: ToastTone;
  description: string;
  /** Jak w `ToastOptions.progress`: brak — bez paska, `null` — pasek nieokreślony. */
  progress?: number | null;
  /** Stan końcowy: śledzenie się kończy, a toast zostaje na kilka sekund. */
  final: boolean;
}

/**
 * Treść powiadomienia dla stanu materiału. Czysta funkcja, bo to ona decyduje,
 * co widz przeczyta, a stanów jest siedem — łatwo jeden pominąć.
 */
export function describeProgress(item: BatchProgressItem): ProgressMessage {
  switch (item.state) {
    case 'ready':
      return { tone: 'success', description: 'Gotowy do oglądania.', final: true };
    case 'failed':
      return {
        tone: 'error',
        description: `Nie udało się pobrać: ${failureLabel(item.errorCode)}.`,
        final: true,
      };
    case 'queued':
      // Kolejka serii: pobiera po jednym odcinku, więc to stan normalny, nie zacięcie.
      return {
        tone: 'loading',
        description: 'Czeka w kolejce serii — odcinki pobierają się po kolei.',
        final: false,
      };
    case 'probing':
    case 'processing':
      return {
        tone: 'loading',
        description: 'Przygotowuję do odtwarzania…',
        progress: item.stagePercent ?? null,
        final: false,
      };
    default:
      break;
  }

  // `acquiring` — trzy różne sytuacje pod jednym stanem.
  if (item.stage === 'resolve' || (item.stage === null && item.bytesDone === null)) {
    return {
      tone: 'loading',
      description: 'Szukam kopii w źródłach — to może potrwać do pół minuty.',
      progress: null,
      final: false,
    };
  }
  if (item.waiting) {
    return {
      tone: 'loading',
      description: 'Czeka na wolne miejsce — pobieramy jeden plik naraz.',
      final: false,
    };
  }
  const percent =
    item.bytesTotal !== null && item.bytesTotal > 0 && item.bytesDone !== null
      ? Math.min(100, Math.round((item.bytesDone / item.bytesTotal) * 100))
      : null;
  const speed =
    item.speedBps !== null && item.speedBps > 0
      ? ` · ${((item.speedBps * 8) / 1_000_000).toFixed(1)} Mb/s`
      : '';
  return {
    tone: 'loading',
    description: `Pobieram${percent !== null ? ` · ${percent}%` : '…'}${speed}`,
    progress: percent,
    final: false,
  };
}

/** Kody porażki sprzed kaskady nie należą do kodów hosta — mają własne zdania. */
function failureLabel(code: string | null): string {
  if (code === null) return 'nieznany błąd';
  if (code === 'NO_PLAYERS') return 'żadne źródło nie ma kopii tego odcinka';
  if (code === 'ALL_REJECTED') return 'wszystkie kopie oznaczono jako złe';
  if (code === 'QUOTA_EXCEEDED') return 'wyczerpany limit miejsca na koncie';
  const label = ACQUIRE_ERROR_LABELS_PL[code as AcquireErrorCode] as string | undefined;
  return label !== undefined ? label.charAt(0).toLowerCase() + label.slice(1) : code;
}
